import React, { useState } from "react";
import { ExError, useErrorDisplay } from "../components/ErrorDisplay"
import BusySpinner from "../components/BusySpinner";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    ButtonGroup,
    Button,
    Box,
    DialogContent,
    FormControl,
    InputLabel,
    MenuItem,
    MenuList,
    Select,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
// Clients
import ProficiencyClient, { ProficiencyErrors } from "../apiClients/ProficiencyClient";
import TemplateClient, { Template, TemplateErrors } from "../apiClients/TemplateClient";
import ProfileClient, { ProfileErrors, ProfileGraph } from "../apiClients/ProfileClient";
import { AbilityLevel, BehaviorLevel, Proficiency, ProficiencyGraph, ProficiencyType, TemplateRevision2ProficiencyGroup, TemplateRevision2ProficiencyGroupDisplay, UpdateProficiencySelection, WeightSelection } from "../models/dtos";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel'
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import PublishIcon from '@mui/icons-material/Publish';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ProficiencyUpsertPopup from "./ProficiencyUpsertPopup";
import { Securables, SessionStorageItemIds, UpsertAction } from "../Enums";
import { EnumIntToStringArray, EnumStringToStringArray, ExtractForSetError, IsNotEmpty, sleep } from "../Helpers";
import ButtonEx from "../components/ButtonEx";
import ConditionalDisplay from "../components/ConditionalDisplay";
import ProfileUpsertPopup from "./ProfileUpsertPopup";
import { NameIdSlim } from "../models/custom-models";
import ChooseProficienciesPopup from "./ChooseProficiencysPopup";
import PanelSegment from "../components/PanelSegment";
import { I18N } from "../I18N";
import TemplateRevisionClient, { TemplateRevisionErrors } from "../apiClients/TemplateRevisionClient";
import OnlyForRole from "../components/OnlyForRole";
import TemplateUploadUpsertPopup from "../marketplace/TemplateUploadUpsertPopup";
import UploadIcon from '@mui/icons-material/Upload';
import WeightSelectControl from "../components/WeightSelectControl";
import ScoreCard from "../components/ScoreCard";
import ProficiencyGraphsGrid from "./ProficiencyGraphsGrid";
import { DEFAULT_WEIGHT } from "../Constants";
import useViewportMode from "../hooks/useViewportMode";
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";
import PositionClient from "../apiClients/PositionClient";
import TemplateEditorAiPanel, { TemplateEditorAiProposal } from "../components/ai/TemplateEditorAiPanel";
import ContextualAiDialog from "../components/ai/ContextualAiDialog";
import { buildAiTemplatePreview, weightNumberToSelection, weightSelectionToNumber } from "../components/ai/TemplateEditorAiMapping";
import ProficiencyGroupGuidanceCard from "../components/template/ProficiencyGroupGuidanceCard";

const emptyProfile = () => {
    let ret: NameIdSlim[] = [];
    let slim: NameIdSlim = {
        Id: '',
        Name: '',
        IsActive: true,
        IsDeleted: false,
        IsSelected: true
    };
    ret.push(slim);

    return ret;
}

export interface TemplateEditorPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    templateId: string;
    templateRevisionId: string;
    icon?: JSX.Element;
    onPublish?: () => Promise<void>;
    onCreateRevision?: () => Promise<void>;
}

const TemplateEditorPopup = ({ mode, templateId, templateRevisionId, onPublish = async () => { }, onCreateRevision = async () => { }, icon = <DeveloperBoardIcon fontSize='small' />, ...btnProps }: TemplateEditorPopupProps) => {

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();

    const [profileGraph, setProfileGraph] = useState<ProfileGraph>(null);
    const [templateDetails, setTemplateDetails] = useState<Template>(null);
    const [positionTitle, setPositionTitle] = useState<string>('');
    const [positionDescription, setPositionDescription] = useState<string>('');
    const [proficiencyBank, setProficiencyBank] = useState<Proficiency[]>([]);
    const [proficiencyGroups, setProficiencyGroups] = useState<TemplateRevision2ProficiencyGroupDisplay[]>([]);
    const [selectedProficiencyGroup, setSelectedProficiencyGroup] = useState<TemplateRevision2ProficiencyGroupDisplay>();

    const [abilityLevel, setAbilityLevels] = useState([]);
    const [behaviorLevel, setBehaviorLevels] = useState([]);
    const [weightLevel, setWeightLevels] = useState([]);

    const [profiles, setProfiles] = useState<NameIdSlim[]>(emptyProfile);
    const [selectedProfile, setSelectedProfile] = useState<NameIdSlim>(emptyProfile[0]);
    const { isPhone, isTablet, isDesktop, isPortrait, mode: viewportMode } = useViewportMode();
    const compactEditor = isPhone || (isTablet && isPortrait);
    // This dense toolbar is independently useful content. Keep it on its own
    // row so neither viewport geometry nor the floating AI dialog can squeeze
    // the editor title into one-character wrapping.
    const controlsBelowHeader = true;

    const [isEditingProfGroups, setIsEditingProfGroups] = useState<boolean>(false);
    const [addToBank, setAddToBank] = useState<boolean>(false);
    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const editorAnchorRef = React.useRef<HTMLDivElement | null>(null);

    var title = I18N.Translate('Template Editor');

    const handleOpen = async () => {
        setOpen(true);
        setIsEditingProfGroups(false);
        setAiDialogOpen(false);
        await loadData(true);
        if (mode !== UpsertAction.View)
            setAiDialogOpen(true);
    };

    const loadData = async (resetSelection: boolean = false, reselectProficiencyGroupId: string = null) => {
        try {
            setBusy(true);

            setAbilityDropdown();
            setBehaviorDropDown();
            setWeightSelectionLevels();

            let sp = await refreshSelectedProfile();
            if (!sp)
                throw new Error('This template revision does not have a profile to edit.');

            // Get a Profile Graph
            let nextProfileGraph = await new ProfileClient().GetSingleGraph(sp.Id);
            setProfileGraph(nextProfileGraph);

            // Proficiency Bank
            let p = await new ProficiencyClient().GetAll();
            setProficiencyBank(p);

            // List of Proficiency Groups / Criteria Categories
            setProficiencyGroups(nextProfileGraph.TemplateRevision2ProficiencyGroupDisplays);

            if (resetSelection) {
                const nextTemplateDetails = await new TemplateClient().GetSingle(templateId);
                setTemplateDetails(nextTemplateDetails);
                if (IsNotEmpty(nextTemplateDetails.PositionId)) {
                    try {
                        const position = await new PositionClient().GetSingle(nextTemplateDetails.PositionId);
                        setPositionTitle(position.Title?.trim() || 'Position unavailable');
                        setPositionDescription(position.Description ?? '');
                    } catch {
                        // A deleted or temporarily unavailable position should not
                        // make the template itself impossible to inspect or edit.
                        setPositionTitle('Position unavailable');
                        setPositionDescription('');
                    }
                } else {
                    setPositionTitle('Unassigned');
                    setPositionDescription('');
                }
            }

            // if the form is first loading, then select the first proficiency group
            if (resetSelection && nextProfileGraph.TemplateRevision2ProficiencyGroupDisplays.length > 0)
                setSelectedProficiencyGroup(nextProfileGraph.TemplateRevision2ProficiencyGroupDisplays[0]);
            // if the user has adjusted the weight of the proficiency group then get the updated value
            else if (IsNotEmpty(reselectProficiencyGroupId))
                setSelectedProficiencyGroup(nextProfileGraph.TemplateRevision2ProficiencyGroupDisplays.find(f => f.Id === reselectProficiencyGroupId));

        } catch (error) {
            setError(error, ProfileErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    const handleRemove = async (ap: ProficiencyGraph) => {
        try {
            setBusy(true);

            await new ProficiencyClient().Delete(ap.ProficiencyId);
            await loadData();

        } catch (error) {
            setError(error, ProfileErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    const handleProficiencyCreate = async (p: Proficiency) => {
        try {
            setBusy(true);

            let pg = proficiencyGroups.find(x => x.ProficiencyGroupId === p.ProficiencyGroupId);
            createProficiency(p, pg);

        } catch (error) {
            setError(error, ProfileErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    const handleProficiencyAddFromBank = async (pg: TemplateRevision2ProficiencyGroupDisplay, newProficiencies: string[]) => {
        try {
            setBusy(true);

            await Promise.all(
                newProficiencies.map(np => {
                    let p = proficiencyBank.find(x => x.Id === np);
                    return createProficiency(p, pg, false);
                })
            );
            await loadData();

        } catch (error) {
            setError(error, ProfileErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    const createProficiency = async (p: Proficiency, pg: TemplateRevision2ProficiencyGroupDisplay, reload: boolean = true) => {

        let createProficiency = {
            Name: p.Name,
            Type: p.Type,
            IsNegative: p.IsNegative,
            Weight: DEFAULT_WEIGHT,
            ProficiencyGroupId: null,
            TemplateRevisionId: null,
            AssociationId: null
        };

        if (addToBank) {
            // Add to the proficiency bank
            await new ProficiencyClient().Create(createProficiency);
        }

        // Add a copy to the Template Revision
        createProficiency.ProficiencyGroupId = pg.ProficiencyGroupId;
        createProficiency.TemplateRevisionId = templateRevisionId;
        createProficiency.AssociationId = selectedProfile.Id;
        await new ProficiencyClient().Create(createProficiency);

        if (reload)
            loadData();
    }

    const handleSelectionChanged = async (graph: ProficiencyGraph, formSelection: string) => {
        try {
            setBusy(true);

            // Update the Proficiency Selection, temporarily so it doesn't look wierd in the UI
            const tempGraph = [...profileGraph.Proficiencies];
            const index = tempGraph.findIndex(x => x.ProficiencyId === graph.ProficiencyId);
            tempGraph[index].Selection.SelectionText = formSelection;

            let updateDto: UpdateProficiencySelection = {
                ProficiencyId: graph.ProficiencyId,
                ProficiencySelectionId: graph.ProficiencySelectionId,
                Selection: formSelection,
                Notes: null
            }

            await new ProfileClient().UpdateProficiency(profileGraph.Profile.Id, updateDto);
            refreshProfileGraph();

            // keep the busy spinner up until the profileGraph is refreshed
            await sleep(500);

        } catch (error) {
            setError(error, ProficiencyErrors.Update);
        }
        finally {
            setBusy(false);
        }
    };

    const handleSetWeight = async (proficiencyId: string, weight: string) => {
        try {
            setBusy(true);

            const setWeightContext = {
                ProficiencyId: proficiencyId,
                Weight: weight
            };

            await new ProfileClient().SetProficiencyWeight(profileGraph.Profile.Id,setWeightContext);
            refreshProfileGraph();

        } catch (error) {
            const exError = error as ExError;
            exError.userFriendlyMessage = 'Oh no, something went wrong setting the proficiency weight. Please try again. If the problem persists, create a request from the Requests page.';
            exError.componentName = 'TemplateEditorPopup.HandleSetWeight';
            exError.jsonMethodParams = { ProficiencyId: proficiencyId, NewWeightValue: weight.toString() };
            setError(error, ProficiencyErrors.Update);
        }
        finally {
            setBusy(false);
        }

    };

    const setAbilityDropdown = () => setAbilityLevels(EnumIntToStringArray(AbilityLevel));

    const setBehaviorDropDown = () => setBehaviorLevels(EnumIntToStringArray(BehaviorLevel));

    const setWeightSelectionLevels = () => setWeightLevels(EnumStringToStringArray(WeightSelection));

    const refreshProfileGraph = async () => {
        try {
            setBusy(true);
            let graph = await new ProfileClient().GetSingleGraph(selectedProfile.Id);
            setProfileGraph(graph);
        } catch (error) {
            setError(error, ProfileErrors.GetSingle);
        } finally {
            setBusy(false);
        }
    }

    const handleProfileChange = async (profileId) => {
        try {
            setBusy(true);

            let graph = await new ProfileClient().GetSingleGraph(profileId);
            setProfileGraph(graph);
            setProficiencyGroups(graph.TemplateRevision2ProficiencyGroupDisplays);
            setSelectedProficiencyGroup(
                graph.TemplateRevision2ProficiencyGroupDisplays.find(group =>
                    group.ProficiencyGroupId === selectedProficiencyGroup?.ProficiencyGroupId
                ) ?? graph.TemplateRevision2ProficiencyGroupDisplays[0]
            );
            setAiDialogOpen(false);

            localStorage.setItem(SessionStorageItemIds.SelectedProfileId, profileId);

            await refreshSelectedProfile();

        } catch (error) {
            setError(error, ProfileErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    }

    const refreshSelectedProfile = async () => {
        // Get the Profiles
        const apiResponse = await new TemplateRevisionClient().GetAll(templateRevisionId);
        if (ExtractForSetError(apiResponse, ProfileErrors.GetAll, setError))
            return;

        const availableProfiles = apiResponse.Content ?? [];
        setProfiles(availableProfiles);

        let selectedProfileId = localStorage.getItem(SessionStorageItemIds.SelectedProfileId);
        let profile = availableProfiles.find(x => x.Id === selectedProfileId);
        if (profile === undefined) {
            profile = availableProfiles[0];
        }
        setSelectedProfile(profile);

        return profile;
    }

    const handlePublish = async () => {
        try {
            setBusy(true);
            await new ProfileClient().Publish(selectedProfile.Id);

            setOpen(false);
            onPublish();

        } catch (error) {
            setError(error, TemplateErrors.Publish);
        }
        finally {
            setBusy(false);
        }
    }

    const handleCreateRevision = async () => {
        try {
            setBusy(true);
            await new TemplateRevisionClient().Create(templateRevisionId);

            setOpen(false);
            onCreateRevision();

        } catch (error) {
            setError(error, TemplateRevisionErrors.Create);
        }
        finally {
            setBusy(false);
        }
    }

    const handleSetProficiencyGroupWeight = async (pg: TemplateRevision2ProficiencyGroupDisplay, weight: number) => {
        try {
            setBusy(true);

            const mapping: TemplateRevision2ProficiencyGroup = {
                Id: pg.Id,
                TemplateRevisionId: pg.TemplateRevisionId,
                ProficiencyGroupId: pg.ProficiencyGroupId,
                Weight: weight
            }

            await new TemplateRevisionClient().UpdateWeight(templateId, mapping);

            setIsEditingProfGroups(false);
            await loadData(false, selectedProficiencyGroup.Id);

        } catch (error) {
            setError(error, TemplateRevisionErrors.UpdateWeight);
        }
        finally {
            setBusy(false);
        }
    }

    const handleAcceptAiProposal = async (proposal: TemplateEditorAiProposal) => {
        if (!selectedProfile?.Id)
            throw new Error('Select a template profile before applying an AI proposal.');

        if (proposal.Type === 'add') {
            if (!proposal.Item || !proposal.GroupId)
                throw new Error('The proposed proficiency no longer has a valid target group.');
            if (!isTemplateWeight(proposal.Item.Weight))
                throw new Error('The proposed proficiency weight is outside the supported range.');
            if (profileGraph.Proficiencies.some(proficiency =>
                proficiency.ProficiencyGroupId === proposal.GroupId
                && proficiency.Type === proposal.Item.Type
                && proficiency.IsNegative === proposal.Item.IsNegative
                && normalizeProficiencyName(proficiency.Name) === normalizeProficiencyName(proposal.Item.Description)
            ))
                throw new Error('That proficiency was already added after this proposal was generated.');

            const proficiencyType = proposal.Item.Type === 'Ability'
                ? ProficiencyType.Ability
                : proposal.Item.Type === 'Behavior'
                    ? ProficiencyType.Behavior
                    : null;
            if (!proficiencyType)
                throw new Error('The proposed proficiency type is not supported.');

            await new ProficiencyClient().Create({
                Name: proposal.Item.Description,
                Type: proficiencyType,
                IsNegative: proposal.Item.IsNegative,
                Weight: proposal.Item.Weight,
                ProficiencyGroupId: proposal.GroupId,
                TemplateRevisionId: templateRevisionId,
                AssociationId: selectedProfile.Id,
            });
        } else if (proposal.Type === 'remove') {
            if (!proposal.ProficiencyId)
                throw new Error('The proficiency no longer exists in this template revision.');
            const currentProficiency = profileGraph.Proficiencies.find(proficiency => proficiency.ProficiencyId === proposal.ProficiencyId);
            if (!currentProficiency
                || !proposal.Item
                || currentProficiency.Type !== proposal.Item.Type
                || currentProficiency.IsNegative !== proposal.Item.IsNegative
                || normalizeProficiencyName(currentProficiency.Name) !== normalizeProficiencyName(proposal.Item.Description)
                || weightSelectionToNumber(currentProficiency.Weight) !== proposal.CurrentWeight)
                throw new Error('The proficiency changed after this proposal was generated.');
            await new ProficiencyClient().Delete(proposal.ProficiencyId);
        } else if (proposal.Type === 'proficiency-weight') {
            if (!proposal.ProficiencyId || !isTemplateWeight(proposal.ProposedWeight))
                throw new Error('The proficiency weight proposal is incomplete.');
            const currentProficiency = profileGraph.Proficiencies.find(proficiency => proficiency.ProficiencyId === proposal.ProficiencyId);
            if (!currentProficiency || weightSelectionToNumber(currentProficiency.Weight) !== proposal.CurrentWeight)
                throw new Error('The proficiency weight changed after this proposal was generated.');
            await new ProfileClient().SetProficiencyWeight(selectedProfile.Id, {
                ProficiencyId: proposal.ProficiencyId,
                Weight: weightNumberToSelection(proposal.ProposedWeight),
            });
        } else if (proposal.Type === 'group-weight') {
            if (!proposal.GroupId || !isTemplateWeight(proposal.ProposedWeight))
                throw new Error('The group weight proposal is incomplete.');
            const mapping = proficiencyGroups.find(group => group.ProficiencyGroupId === proposal.GroupId);
            if (!mapping || mapping.Weight !== proposal.CurrentWeight)
                throw new Error('The proficiency group changed after this proposal was generated.');
            await new TemplateRevisionClient().UpdateWeight(templateId, {
                Id: mapping.Id,
                TemplateRevisionId: mapping.TemplateRevisionId,
                ProficiencyGroupId: mapping.ProficiencyGroupId,
                Weight: proposal.ProposedWeight,
            });
        }

        await loadData(false, selectedProficiencyGroup?.Id);
    };

    const aiAvailable = mode !== UpsertAction.View && Boolean(profileGraph && templateDetails && selectedProfile?.Id);
    const currentAiPreview = aiAvailable
        ? buildAiTemplatePreview(profileGraph, templateDetails, positionDescription)
        : null;
    const currentGroupIds = Object.fromEntries(
        (profileGraph?.TemplateRevision2ProficiencyGroupDisplays ?? [])
            .map(group => [group.ProficiencyGroupName, group.ProficiencyGroupId])
    );

    const editorControls = (
        <Box
            data-testid="template-editor-controls"
            sx={{
                width: '100%',
                minWidth: 0,
                display: 'grid',
                gridTemplateColumns: compactEditor ? '1fr' : 'minmax(190px, 0.8fr) minmax(360px, 2fr) minmax(190px, 0.8fr)',
                alignItems: 'center',
                gap: 1.5,
                '& .MuiButtonBase-root, & .MuiInputBase-root': { minHeight: isDesktop ? undefined : 44 },
            }}
        >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
                    <InputLabel id="profile-select-label">Profile</InputLabel>
                    <Select
                        labelId="profile-select-label"
                        id="profile-select"
                        label="Profile"
                        value={selectedProfile?.Id ?? ''}
                        inputProps={{ 'aria-label': 'Template profile' }}
                        onChange={(event) => handleProfileChange(event.target.value)}>
                        {profiles?.map((profile) => <MenuItem key={profile.Id} value={profile.Id}>{profile.Name}</MenuItem>)}
                    </Select>
                </FormControl>
                <ConditionalDisplay ifTrue={mode === UpsertAction.Create || mode === UpsertAction.Update}>
                    <ProfileUpsertPopup
                        mode={UpsertAction.Create}
                        templateRevisionId={profileGraph?.TemplateRevisionId}
                        profileId={selectedProfile?.Id}
                        onAdd={(id) => handleProfileChange(id)}
                        hoverContent={I18N.Translate('Add Profile')}
                        aria-label={I18N.Translate('Add Profile')}
                        icon={<AddIcon fontSize="small" />}
                    />
                </ConditionalDisplay>
            </Stack>
            <Box sx={{ minWidth: 0, justifySelf: 'center' }}><ScoreCard profileGraph={profileGraph} /></Box>
            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                {aiAvailable && <Button size="small" variant="outlined" startIcon={<AutoAwesomeRounded />} onClick={() => setAiDialogOpen(true)}>Open Synergist AI</Button>}
                <ConditionalDisplay ifTrue={mode === UpsertAction.Create || mode === UpsertAction.Update}>
                    <OnlyForRole role={Securables.template_publish}>
                        <IconButtonEx
                            size='small'
                            hoverContent={I18N.Translate('Publish Template')}
                            message={I18N.Translate('Are you sure you want to Publish this Template and make it active?')}
                            onClick={handlePublish}
                        >
                            <PublishIcon htmlColor='green' fontSize='small' />
                        </IconButtonEx>
                    </OnlyForRole>
                </ConditionalDisplay>
            </Stack>
        </Box>
    );

    return <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={title} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog
            aria-labelledby="customized-dialog-title"
            open={open}
            fullWidth
            maxWidth='xl'
            widthTier="wide"
            sizingStrategy="workspace"
            desktopViewportGutter={32}
            onClose={() => setOpen(false)}
            PaperProps={{
                sx: {
                    // Give percentage-height editor children a definite viewport
                    // boundary. Without this, a short desktop dialog can size to
                    // the AI conversation and clip its composer below the paper.
                    height: 'calc(100% - 64px)',
                    maxHeight: 'calc(100% - 64px)',
                    overflow: 'hidden',
                },
            }}
        >
            <ResponsiveDialogHeader
                id="customized-dialog-title"
                title={
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                            {title}
                        </Typography>
                        <Tooltip title={templateDetails?.Name ?? ''} enterDelay={0} disableHoverListener={!templateDetails?.Name}>
                            <Typography
                                data-testid="template-editor-template-name"
                                noWrap
                                tabIndex={templateDetails?.Name ? 0 : undefined}
                                aria-label={templateDetails?.Name ? `Template: ${templateDetails.Name}` : 'Loading template'}
                                sx={{
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: { xs: '1rem', sm: '1.35rem' },
                                    fontWeight: 700,
                                    lineHeight: 1.25,
                                }}
                            >
                                {templateDetails?.Name || 'Loading template…'}
                            </Typography>
                        </Tooltip>
                    </Stack>
                }
                subtitle={
                    <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ flex: '0 0 auto', fontWeight: 700 }}>
                            Position
                        </Typography>
                        <Tooltip title={positionTitle} enterDelay={0} disableHoverListener={!positionTitle}>
                            <Typography
                                component="span"
                                data-testid="template-editor-position-name"
                                noWrap
                                tabIndex={positionTitle ? 0 : undefined}
                                aria-label={`Assigned position: ${positionTitle || 'Loading'}`}
                                sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                                {positionTitle || 'Loading…'}
                            </Typography>
                        </Tooltip>
                    </Stack>
                }
                actions={!controlsBelowHeader ? editorControls : undefined}
                onClose={() => setOpen(false)}
                viewportOverride={viewportMode}
            />
            <DialogContent
                dividers
                sx={{
                    padding: compactEditor ? 0 : theme.spacing(2),
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {controlsBelowHeader && <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>{editorControls}</Box>}
                <Accordion
                    disableGutters
                    elevation={0}
                    data-testid="template-editor-position-description"
                    sx={{
                        flex: '0 0 auto',
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&::before': { display: 'none' },
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-label="View position job description (read only)"
                        aria-controls="template-editor-position-description-content"
                        id="template-editor-position-description-header"
                        sx={{ minHeight: isPhone ? 48 : 44, px: compactEditor ? 2 : 2.5 }}
                    >
                        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
                            <Typography fontWeight={700}>Position job description</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Read only
                            </Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails
                        id="template-editor-position-description-content"
                        role="region"
                        aria-label="Assigned position job description"
                        sx={{ px: compactEditor ? 2 : 2.5, pt: 0, pb: 2, maxHeight: 'min(32vh, 280px)', overflowY: 'auto' }}
                    >
                        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                            {positionDescription?.trim() || (positionTitle === 'Unassigned'
                                ? 'This template is not assigned to a position.'
                                : 'No job description is available for this position.')}
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
                <BusySpinner busy={busy}>
                    <Box
                        ref={editorAnchorRef}
                        data-testid="template-editor-workspace"
                        data-layout={compactEditor ? 'compact' : isTablet ? 'tablet-landscape' : 'desktop'}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            height: '100%',
                            display: 'grid',
                            gridTemplateColumns: compactEditor
                                ? '1fr'
                                : isTablet
                                    ? 'minmax(220px, 0.35fr) minmax(0, 1fr)'
                                    : 'minmax(180px, 0.2fr) minmax(0, 1fr)',
                            gridTemplateRows: compactEditor ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
                            gap: compactEditor ? 0 : 2,
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ minWidth: 0, overflow: compactEditor ? 'hidden' : 'auto' }}>
                            <PanelSegment titleContent={
                                <Stack direction="row" sx={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between', textAlign: 'center' }}>
                                    <Typography>Proficiency Groups</Typography>
                                    <ConditionalDisplay ifTrue={!isEditingProfGroups}>
                                        <OnlyForRole role={Securables.template_modify}>
                                            <IconButtonEx onClick={() => setIsEditingProfGroups(true)}><EditIcon htmlColor='lightgreen' fontSize='small' /></IconButtonEx>
                                        </OnlyForRole>
                                    </ConditionalDisplay>
                                    <ConditionalDisplay ifTrue={isEditingProfGroups}>
                                        <IconButtonEx onClick={() => setIsEditingProfGroups(false)}><CancelIcon htmlColor='red' fontSize='small' /></IconButtonEx>
                                    </ConditionalDisplay>
                                </Stack>
                            }>
                                <MenuList sx={{ display: compactEditor ? 'flex' : 'block', overflowX: compactEditor ? 'auto' : 'visible', whiteSpace: compactEditor ? 'nowrap' : 'normal', p: compactEditor ? 0.5 : 0 }}>
                                    {profileGraph?.TemplateRevision2ProficiencyGroupDisplays.map((pg) =>
                                        <MenuItem
                                            key={pg.Id}
                                            onClick={() => setSelectedProficiencyGroup(pg)}
                                            selected={pg.Id === selectedProficiencyGroup?.Id}
                                            sx={{
                                                minWidth: compactEditor ? 180 : 0,
                                                maxWidth: compactEditor ? 280 : '100%',
                                                minHeight: isDesktop ? undefined : 48,
                                                flex: compactEditor ? '0 0 auto' : undefined,
                                                overflow: 'hidden',
                                            }}>
                                            <Stack direction="row" sx={{ flex: 1, minWidth: 0, maxWidth: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                <Tooltip title={pg.ProficiencyGroupName} enterDelay={0}>
                                                    <Typography
                                                        noWrap
                                                        tabIndex={0}
                                                        aria-label={`Full proficiency group name: ${pg.ProficiencyGroupName}`}
                                                        sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {pg.ProficiencyGroupName}
                                                    </Typography>
                                                </Tooltip>
                                                <ConditionalDisplay ifTrue={!isEditingProfGroups}>
                                                    <Typography fontSize="small" sx={{ flex: '0 0 auto' }}>{pg.Weight}</Typography>
                                                </ConditionalDisplay>
                                                <ConditionalDisplay ifTrue={isEditingProfGroups}>
                                                    <WeightSelectControl
                                                        mode={mode}
                                                        key={pg.ProficiencyGroupId}
                                                        value={pg?.Weight}
                                                        onChangeHandler={(e) => handleSetProficiencyGroupWeight(pg, e.target.value)} />
                                                </ConditionalDisplay>
                                            </Stack>
                                        </MenuItem>
                                    )}
                                </MenuList>
                            </PanelSegment>
                        </Box>
                        <Box sx={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
                            <ConditionalDisplay ifTrue={IsNotEmpty(selectedProficiencyGroup)}>
                                {() =>
                                    <PanelSegment
                                        boundedHeight
                                        titleContent={
                                            <Stack direction="row" sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', alignItems: 'center' }}>
                                                <Tooltip title={selectedProficiencyGroup.ProficiencyGroupName} enterDelay={0}>
                                                    <Typography
                                                        noWrap
                                                        tabIndex={0}
                                                        aria-label={`Selected proficiency group: ${selectedProficiencyGroup.ProficiencyGroupName}`}
                                                        sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {selectedProficiencyGroup.ProficiencyGroupName}
                                                    </Typography>
                                                </Tooltip>
                                                {/* <ConditionalDisplay ifTrue={!isEditingProfGroups}>
                                                    <Typography sx={{ marginLeft: '.5rem', fontSize: 'small' }}>{`(${selectedProficiencyGroup.Weight})`}</Typography>
                                                </ConditionalDisplay> */}
                                                {/* <ConditionalDisplay ifTrue={isEditingProfGroups}>
                                                    <Input name="Weight"
                                                        sx={{ marginLeft: '.5rem', fontSize: 'small' }}
                                                        type="number"
                                                        value={selectedProficiencyGroup?.Weight ?? 1}
                                                        inputProps={{ min: 1, max: 9 }}
                                                        onChange={(e) => handleSetProficiencyGroupWeight(selectedProficiencyGroup, Number.parseInt(e.target.value))} />
                                                </ConditionalDisplay> */}
                                                {/* <ConditionalDisplay ifTrue={!isEditingProfGroups}>
                                                    <IconButtonEx onClick={() => setIsEditingProfGroups(true)}><EditIcon htmlColor='lightgreen' fontSize='small' /></IconButtonEx>
                                                </ConditionalDisplay>
                                                <ConditionalDisplay ifTrue={isEditingProfGroups}>
                                                    <IconButtonEx onClick={() => setIsEditingProfGroups(false)}><CancelIcon htmlColor='red' fontSize='small' /></IconButtonEx>
                                                </ConditionalDisplay> */}

                                            </Stack>
                                        }
                                        toolbar={
                                            <ButtonGroup aria-label="Proficiency actions">
                                                <ConditionalDisplay ifTrue={mode === UpsertAction.Create || mode === UpsertAction.Update}>
                                                    <>
                                                        <OnlyForRole role={Securables.proficiency_create}>
                                                            <ProficiencyUpsertPopup
                                                                mode={UpsertAction.Create}
                                                                onCreate={handleProficiencyCreate}
                                                                showAddToBankCheckbox
                                                                addToBank={addToBank}
                                                                onAddToBankChange={(checked) => setAddToBank(checked)}
                                                                hoverContent={I18N.Translate('Add New')}
                                                                visibleLabel={I18N.Translate('Add New')}
                                                                aria-label={I18N.Translate('Add New')}
                                                            />
                                                        </OnlyForRole>
                                                        <ChooseProficienciesPopup
                                                            proficiencies={proficiencyBank}
                                                            excludeIds={profileGraph.Proficiencies.filter(x => x.ProficiencyGroupId === selectedProficiencyGroup.ProficiencyGroupId).map(x => x.ProficiencyId)}
                                                            onAdd={ids => handleProficiencyAddFromBank(selectedProficiencyGroup, ids)}
                                                            visibleLabel={I18N.Translate('Add Existing')}
                                                            aria-label={I18N.Translate('Add Existing')} />
                                                    </>
                                                </ConditionalDisplay>
                                            </ButtonGroup>
                                        }>
                                        <Box
                                            data-testid="template-category-content-scroll"
                                            role="region"
                                            tabIndex={0}
                                            aria-label={`${selectedProficiencyGroup.ProficiencyGroupName} guidance and proficiencies`}
                                            sx={{
                                                height: '100%',
                                                minHeight: 0,
                                                width: '100%',
                                                display: 'grid',
                                                gridTemplateRows: 'auto minmax(240px, 1fr)',
                                                overflowX: 'hidden',
                                                overflowY: 'auto',
                                                WebkitOverflowScrolling: 'touch',
                                                overscrollBehavior: 'contain',
                                                '&:focus-visible': {
                                                    outline: '2px solid',
                                                    outlineColor: 'primary.main',
                                                    outlineOffset: -2,
                                                },
                                            }}
                                        >
                                            <ProficiencyGroupGuidanceCard groupName={selectedProficiencyGroup.ProficiencyGroupName} />
                                            <Box sx={{ height: '100%', minHeight: 240, overflow: 'hidden', pt: 1 }}>
                                                <ProficiencyGraphsGrid
                                                    profileGraph={profileGraph}
                                                    selectedProficiencyGroup={selectedProficiencyGroup}
                                                    mode={mode}
                                                    loadData={loadData}
                                                    handleSetWeight={handleSetWeight}
                                                    handleSelectionChanged={handleSelectionChanged}
                                                    handleRemove={handleRemove}
                                                    abilityLevel={abilityLevel}
                                                    behaviorLevel={behaviorLevel}
                                                    weightSelections={weightLevel}
                                                />
                                            </Box>
                                        </Box>
                                    </PanelSegment>
                                }
                            </ConditionalDisplay>

                        </Box>
                    </Box>
                    {aiAvailable && currentAiPreview && (
                        <ContextualAiDialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} anchorEl={editorAnchorRef.current} title="Synergist AI — Template Editor" workflow="template-editor">
                            <TemplateEditorAiPanel
                                key={`${templateRevisionId}|${selectedProfile.Id}`}
                                positionId={templateDetails.PositionId}
                                positionTitle={positionTitle}
                                templateName={templateDetails.Name}
                                profileName={selectedProfile.Name}
                                currentPreview={currentAiPreview}
                                currentGroupIds={currentGroupIds}
                                activeCategoryName={selectedProficiencyGroup?.ProficiencyGroupName}
                                onAcceptProposal={handleAcceptAiProposal}
                            />
                        </ContextualAiDialog>
                    )}

                </BusySpinner >
                </Box>

            </DialogContent>
            <StickyDialogActions viewportOverride={viewportMode}>
                <ButtonEx autoFocus onClick={() => setOpen(false)} variant='text' color='inherit'>Close</ButtonEx>
                <ConditionalDisplay ifTrue={mode === UpsertAction.View}>
                    <Stack direction='row'>
                        <OnlyForRole role={Securables.template_publish}>
                            <ButtonEx
                                onClick={handleCreateRevision}
                                message={I18N.Translate('Do you want to continue creating a New Revision of this Template?')}
                                variant='outlined'
                                color='primary'>Create Revision</ButtonEx>
                        </OnlyForRole>
                        <OnlyForRole role={Securables.synergist_admin}>
                            <TemplateUploadUpsertPopup icon={<UploadIcon sx={{ color: 'inherit' }} fontSize='small' />} mode={UpsertAction.Create} templateRevisionId={templateRevisionId} />
                            {/* <ButtonEx
                                onClick={() => }
                                message={I18N.Translate('Do you want to continue uploading this Template to the Marketplace?')}
                                variant='outlined'
                                color='primary'>Upload to Marketplace</ButtonEx> */}
                        </OnlyForRole>
                    </Stack>
                </ConditionalDisplay>
            </StickyDialogActions>
        </ResponsiveDialog>
    </>;
}

const normalizeProficiencyName = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
const isTemplateWeight = (value: number | undefined): value is number =>
    Number.isInteger(value) && value >= 1 && value <= 10;

export default TemplateEditorPopup;
